import type { ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface TableToolbarProps {
  /** Left-hand elements (Search input, FilterPill items, AddFilterMenu) */
  children?: ReactNode;
  /** Right-hand elements (SortDropdown, ViewToggle, etc.) */
  actions?: ReactNode;
  /** Summary count or label (e.g. "12 builds" or "4 active") */
  count?: ReactNode;
  /** Whether filters/search are currently active (diverging from default) */
  hasActiveFilters?: boolean;
  /** Callback to clear all active filters and search */
  onResetFilters?: () => void;
  /**
   * Container styling variant:
   * - "page" (default): Floating above list/cards in page content area
   * - "card": Embedded inside a table container card header with border-b
   */
  variant?: "page" | "card";
  className?: string;
}

export function TableToolbar({
  children,
  actions,
  count,
  hasActiveFilters,
  onResetFilters,
  variant = "page",
  className,
}: TableToolbarProps) {
  return (
    <div
      data-slot="table-toolbar"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3",
        variant === "card" &&
          "border-b border-border/80 bg-muted/40 px-4 py-2.5",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 basis-full flex-wrap items-center gap-2 sm:basis-0">
        {count && (
          <div className="mr-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground shrink-0">
            {count}
          </div>
        )}
        {children}
        {hasActiveFilters && onResetFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="h-9 gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="size-3" aria-hidden="true" />
            <span>Reset filters</span>
          </Button>
        )}
      </div>

      {actions && (
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          {actions}
        </div>
      )}
    </div>
  );
}
